/**
 * La coquille du back-office : barre latérale, barre supérieure, contenu.
 *
 * Support : **web responsive 1440 × 900, thème clair uniquement**, densité
 * maximale, échelle typographique réduite d'un cran par rapport au mobile.
 *
 * La navigation **dérive de la table des modules** : les seize destinations
 * sont atteignables, groupées et ordonnées comme dans la maquette, et ajouter
 * un module ne demande de toucher qu'à `routes.ts`. Une navigation écrite à la
 * main à côté de la table serait une seconde source de vérité, et les deux
 * finiraient par diverger.
 *
 * Chaque entrée porte l'icône de la maquette et, **si une donnée le fournit**,
 * un compteur. Les nombres de la maquette — 18 dossiers, 9 alertes — ne sont
 * écrits nulle part dans le code : voir `module-badges.tsx`.
 */

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useModuleBadgeCounts } from './module-badges.js';
import { ModuleIcon } from './module-icons.js';
import {
  isModuleUsable,
  moduleGroups,
  moduleNavLabelKey,
  modulesOfGroup,
  moduleSearchPlaceholderKeys,
  moduleSubtitleKey,
  moduleTitleKey,
} from './routes.js';
import type { BackOfficeModule } from './routes.js';
import { ExportProvider, useTopbarExport } from './export-context.js';
import { SearchProvider, useTopbarSearch } from './search-context.js';
import { useCurrentModule } from './useCurrentModule.js';
import { useApiClient } from '../http/api-context.js';
import { fetchOperatorModules } from '../http/back-office-modules.js';
import { useSession, useSessionStore } from '../state/session-context.js';
import type { OperatorSession } from '../state/session-store.js';

/**
 * Le compteur d'un module, quand une donnée en fournit un.
 *
 * Le nombre est **rendu deux fois** : une fois pour l'œil, une fois pour le
 * lecteur d'écran, avec ce qu'il compte. Un « 18 » seul, annoncé à la suite du
 * libellé, ne dit pas s'il s'agit de dossiers en attente ou d'un numéro de
 * module.
 */
function ModuleBadge({ count }: { readonly count: number }): ReactNode {
  const { t } = useTranslation();
  return (
    <span className="shell__nav-badge">
      <span aria-hidden="true">{count}</span>
      <span className="shell__nav-badge-reading">{t('nav.badgeCount', { count })}</span>
    </span>
  );
}

/** Une entrée de la barre latérale : icône, libellé, et compteur s'il y en a un. */
function ModuleLink({ module }: { readonly module: BackOfficeModule }): ReactNode {
  const { t } = useTranslation();
  const compte = useModuleBadgeCounts().get(module.id);
  return (
    <NavLink
      className={({ isActive }) =>
        isActive ? 'shell__nav-link shell__nav-link--active' : 'shell__nav-link'
      }
      to={module.path}
    >
      <ModuleIcon name={module.icon} />
      <span className="shell__nav-label">{t(moduleNavLabelKey(module))}</span>
      {compte === undefined ? null : <ModuleBadge count={compte} />}
    </NavLink>
  );
}

/**
 * Lit une fois, par session ouverte, ce que la matrice dit des modules.
 *
 * **Un échec ne casse rien et ne masque rien.** Le magasin reste à `null`, et
 * [isModuleUsable] rend alors tous les modules : un menu qui se viderait parce qu'une
 * requête a mal tourné laisserait un opérateur habilité devant rien, sans explication.
 * Le serveur, lui, refuse par ses propres contrôles quoi qu'il arrive (`EX-BO-002`).
 */
function useOperatorModules(): void {
  const client = useApiClient();
  const magasin = useSessionStore();
  const connecte = useSession((etat) => etat.operator !== null);
  const dejaLu = useSession((etat) => etat.modules !== null);

  useEffect(() => {
    if (!connecte || dejaLu) return;
    let abandonne = false;
    void fetchOperatorModules(client)
      .then((modules) => {
        // `null` : le serveur a répondu autre chose que la forme attendue. Le magasin
        // reste vide, et le menu reste entier — voir `fetchOperatorModules`.
        if (!abandonne && modules !== null) magasin.getState().setModules(modules);
      })
      .catch(() => {
        // Volontairement muet : voir l'en-tête. Rien à dire à l'opérateur, dont le
        // menu reste entier et dont chaque clic sera arbitré par le serveur.
      });
    return () => {
      abandonne = true;
    };
  }, [client, connecte, dejaLu, magasin]);
}

/**
 * La barre latérale : la marque, puis les quatre groupes de modules.
 *
 * **Elle ne propose que ce que l'opérateur peut ouvrir.** `EX-BO-002` a raison de dire
 * que masquer une action dans l'interface ne compte pas comme un contrôle — et la
 * réciproque est vraie aussi : offrir une entrée qui échouera *toujours* n'est pas
 * mieux. Un administrateur technique à qui l'on propose « Identités & KYC » clique,
 * lit un refus, et apprend à se méfier de son propre menu. Le filtre est **une
 * commodité**, jamais une sécurité : la route reste déclarée, l'adresse reste
 * atteignable, et le serveur refuse pareil.
 */
function ModuleNavigation(): ReactNode {
  const { t } = useTranslation();
  const modules = useSession((etat) => etat.modules);
  return (
    <nav className="shell__nav" aria-label={t('nav.landmark')}>
      <div className="shell__brand">
        <span className="shell__brand-mark" aria-hidden="true" />
        <span className="shell__brand-text">
          <span className="shell__brand-name">{t('app.name')}</span>
          <span className="shell__brand-environment">{t('app.environment')}</span>
        </span>
      </div>

      {moduleGroups.map((groupe) => (
        <div className="shell__nav-group" key={groupe}>
          <h2 className="shell__nav-group-title">{t(`nav.groups.${groupe}`)}</h2>
          <ul className="shell__nav-list">
            {modulesOfGroup(groupe)
              .filter((module) => isModuleUsable(module, modules))
              .map((module) => (
                <li key={module.id}>
                  <ModuleLink module={module} />
                </li>
              ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

/**
 * Les initiales d'un nom affiché, telles que la pastille les porte.
 *
 * Deux au plus — la maquette dessine « AD » pour « A. Diop ». Les points, les
 * traits d'union et les espaces séparent : « A. Diop » donne « AD », et
 * « Ndiaye-Fall Awa » donne « NA » et non « N- ». Un nom d'un seul mot rend
 * une seule lettre plutôt que ses deux premières : « Aw » se lirait comme une
 * syllabe, pas comme une identité.
 *
 * **Aucun repli sur une constante.** Un nom vide rend une chaîne vide, et la
 * pastille se dessine sans lettre : écrire « ? » ou « JK » afficherait sous
 * l'identité de l'opérateur quelque chose que personne n'a fourni.
 */
export function operatorInitials(displayName: string): string {
  return displayName
    .split(/[\s.\-—–']+/u)
    .filter((mot) => mot !== '')
    .slice(0, 2)
    .map((mot) => mot.slice(0, 1).toLocaleUpperCase('fr'))
    .join('');
}

/**
 * Le bloc d'identité : la pastille d'initiales, le nom, le rôle.
 *
 * **Il vient de la session, jamais du module.** La maquette fait varier le rôle
 * par écran — « Agent support » sur Clients, « Auditeur — lecture seule » sur le
 * journal d'audit — mais c'est une convention de dessin : elle illustre quel
 * profil emploie chaque module. Dans le produit, le rôle appartient à la
 * **session** : un auditeur qui ouvre l'écran Clients reste auditeur. Sur un
 * back-office où chaque action est imputée nominativement, un rôle qui change
 * en changeant de page serait faux au point d'être dangereux.
 *
 * **Pourquoi ce bloc existe.** Le socle impute chaque action à un compte — c'est
 * la raison d'être du journal d'audit et de la double validation. Une interface
 * qui n'affiche jamais sous quel compte on travaille rend deux choses
 * impossibles : s'apercevoir qu'on est connecté sous le mauvais compte, et le
 * dire à quelqu'un qui regarde par-dessus l'épaule.
 *
 * **Le rôle arrive en code**, pas en libellé : `LoginResponse.role` porte
 * `SUPPORT_N1`, `AUDITOR`… — le code de `admin.bo_role`. Le libellé français
 * vit donc dans les traductions, et un code inconnu se rend **tel quel** plutôt
 * que vide : un rôle absent de l'écran est pire qu'un rôle brut.
 *
 * **La déconnexion est ici, et pas ailleurs.** Elle appartient au bloc qui dit
 * sous quel compte on travaille : c'est en lisant le nom d'un collègue qu'on
 * s'aperçoit qu'il faut fermer sa session. `logout` existait, était documenté,
 * était éprouvé sur le stockage réel — et **rien ne l'appelait** : un poste
 * partagé n'avait aucun moyen de rendre la main. Elle **efface le stockage**,
 * elle ne se contente pas d'oublier l'objet en mémoire ; sans cela le couple de
 * jetons resterait lisible dans l'onglet pour l'agent suivant, qui est
 * exactement le défaut que la persistance de session avait ouvert.
 *
 * Aucune confirmation : le geste ne perd rien — la session se rouvre en se
 * reconnectant — et une boîte de dialogue de plus s'apprend à valider sans
 * lire. Aucun avertissement non plus au retour sur `B-01` : `logout` remet
 * `endReason` à `null`, parce que l'opérateur qui vient de cliquer sait
 * pourquoi il est revenu.
 */
function OperatorBadge({ operator }: { readonly operator: OperatorSession }): ReactNode {
  const { t } = useTranslation();
  const magasin = useSessionStore();
  const initiales = operatorInitials(operator.displayName);
  return (
    <div className="shell__operator">
      <span className="shell__reading">{t('topbar.operator')}</span>
      <span aria-hidden="true" className="shell__operator-initials">
        {initiales}
      </span>
      <span className="shell__operator-identity">
        <span className="shell__operator-name">{operator.displayName}</span>
        <span className="shell__operator-role">
          {t(`topbar.roles.${operator.role}`, { defaultValue: operator.role })}
        </span>
      </span>
      <button
        className="shell__logout"
        onClick={() => magasin.getState().logout()}
        type="button"
      >
        {t('topbar.logout')}
      </button>
    </div>
  );
}

/**
 * La barre supérieure : le titre du module courant, ses outils, l'opérateur.
 *
 * **Quatre choses y sont dynamiques, et elles ne viennent pas du même endroit.**
 * Le titre, le sous-titre et l'invite de recherche viennent du **module** — la
 * maquette les configure module par module dans sa table `boCfg`. Le nom et le
 * rôle viennent de la **session**. Confondre les deux sources donnerait un rôle
 * qui change en changeant de page ; voir `OperatorBadge`.
 */
function ModuleToolbar(): ReactNode {
  const { t } = useTranslation();
  const module = useCurrentModule();
  const operator = useSession((etat) => etat.operator);
  const exporter = useTopbarExport();
  const rechercher = useTopbarSearch();

  /**
   * Ce qui est tapé dans la barre, tant que rien n'est parti.
   *
   * La saisie vit ici et non dans l'écran : la barre appartient à la coquille,
   * et un écran qui la posséderait devrait la remonter pour l'afficher. Elle
   * est **effacée en changeant de module** — un terme resté au-dessus d'un
   * autre registre ferait croire que la liste en dessous est filtrée par lui.
   */
  const [saisie, setSaisie] = useState('');
  /**
   * Le terme que l'écran dit chercher — et que la barre AFFICHE.
   *
   * Sans lui, la barre gardait sa saisie pendant que l'écran, revenu en
   * arrière, avait cessé de filtrer : le champ affirmait un filtre que la table
   * n'appliquait pas. Un écran vide se remarque ; un écran qui ment, non.
   */
  const terme = rechercher?.term ?? '';
  useEffect(() => {
    setSaisie(terme);
  }, [module?.id, terme]);

  return (
    <header className="shell__topbar">
      <div className="shell__topbar-titles">
        <h1 className="shell__topbar-title">
          {module === undefined ? t('routeError.heading') : t(moduleTitleKey(module))}
        </h1>
        {module === undefined ? null : (
          <p className="shell__topbar-subtitle">{t(moduleSubtitleKey(module))}</p>
        )}
      </div>

      <div className="shell__topbar-tools">
        <div className="shell__topbar-controls" role="toolbar" aria-label={t('topbar.landmark')}>
          <label className="shell__search">
            <span className="shell__reading">{t('topbar.searchLabel')}</span>
            <ModuleIcon className="shell__topbar-icon" name="search" />
            {/* **`Entrée` cherche, et la frappe ne cherche pas.** Chercher est
                un acte journalisé (`EX-BO-004`) : une recherche qui partirait à
                chaque frappe écrirait une entrée d'audit par LETTRE, et le
                journal deviendrait illisible sur l'écran même qui sert à le
                relire. Voir `search-context.tsx`.

                Le champ est contrôlé sur les seize modules, y compris ceux qui
                ne déclarent aucune recherche : la frappe n'y part alors nulle
                part — le comportement d'aujourd'hui, inchangé — mais elle
                s'affiche, comme dans n'importe quel champ. */}
            <input
              className="shell__search-input"
              type="search"
              value={saisie}
              onChange={(evenement) => {
                setSaisie(evenement.target.value);
              }}
              onKeyDown={(evenement) => {
                if (evenement.key !== 'Enter' || rechercher === null) return;
                // Le formulaire de la page ne doit pas partir avec : la barre
                // est au-dessus du contenu, pas dedans.
                evenement.preventDefault();
                rechercher.onSearch(saisie.trim());
              }}
              placeholder={
                module === undefined
                  ? t('topbar.searchPlaceholder')
                  : t(moduleSearchPlaceholderKeys(module))
              }
            />
          </label>
          {/* **Le bouton « Exporter », enfin branché — et rendu seulement là
              où il marche.**

              Il était `<button type="button">` sans le moindre gestionnaire,
              sur les seize pages, écrans provisoires compris : il ne faisait
              rien, et il n'avait jamais rien fait. Le câbler dans la coquille
              était impossible honnêtement — la coquille ne sait pas ce qu'un
              écran tient, et les onze écrans provisoires ne tiennent rien. Le
              retirer partout aurait effacé du dessin ce que deux écrans peuvent
              servir.

              Un écran qui tient des lignes déclare donc son export, et la barre
              l'appelle ; un écran qui ne tient rien n'en déclare aucun, et la
              barre ne dessine pas de bouton. Un bouton qui ne fait rien apprend
              à ne plus cliquer — c'est la conduite déjà tenue quatre fois
              ailleurs, appliquée cette fois à la coquille elle-même. */}
          {exporter === null ? null : (
            <button className="shell__topbar-action" type="button" onClick={exporter}>
              <ModuleIcon className="shell__topbar-icon" name="download" />
              {t('topbar.export')}
            </button>
          )}
        </div>
        {operator === null ? null : <OperatorBadge operator={operator} />}
      </div>
    </header>
  );
}

/** La coquille complète. Le contenu d'un module se rend dans l'`Outlet`. */
export function AppShell(): ReactNode {
  const { t } = useTranslation();
  useOperatorModules();
  return (
    <div className="shell">
      <a className="shell__skip" href="#contenu">
        {t('app.skipToContent')}
      </a>
      <ModuleNavigation />
      {/* Les deux déclarations entourent la barre ET le contenu : c'est
          l'écran, dans l'`Outlet`, qui déclare son export et sa recherche, et
          la barre qui les lit. */}
      <ExportProvider>
        <SearchProvider>
          <div className="shell__column">
            <ModuleToolbar />
            <main className="shell__content" id="contenu" tabIndex={-1}>
              <Outlet />
            </main>
          </div>
        </SearchProvider>
      </ExportProvider>
    </div>
  );
}
